'use client';

import type { PropsWithChildren } from 'react';
import type { Collection } from '@/app/actions/collection';
import type { AnswerType } from '~/model/AnswerType';
import type { CommentType } from '~/model/CommentType';
import type { InteractionType } from '~/model/InteractionType';
import type { QuestionReconstructedType, QuestionType } from '~/model/QuestionType';
import { createContext, useContext } from 'react';
import { useComments } from '@/app/hooks/useComments';

const CommentsContext = createContext<ReturnType<typeof useComments> | undefined>(undefined);

export const useCommentsContext = () => {
  const context = useContext(CommentsContext);
  if (!context) {
    throw new Error('useCommentsContext must be called within CommentsProvider');
  }
  return context;
};

export const CommentsProvider = ({
  children,
  answers,
  interaction,
  question,
  resultId,
}: PropsWithChildren<{
  answers: Collection<AnswerType & CommentType>;
  interaction: InteractionType;
  question: QuestionType | QuestionReconstructedType;
  resultId?: number;
}>) => {
  const value = useComments({ answers, interaction, question, resultId });

  return (
    <CommentsContext.Provider value={value}>
      {children}
    </CommentsContext.Provider>
  );
};
